import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";
import { SERVICES } from "@/lib/services-data";

export default function ServicesPage() {
  return (
    <PageShell testId="services-page">
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Services
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              A complete{" "}
              <span className="text-gradient-brand">growth stack.</span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              Six interlocking services. One unified system engineered to make
              your business the obvious choice in your market.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section pt-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const detailed = s.slug === "seo-geo" || s.slug === "website-development";
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`services-card-${i}`}
                  className="group relative rounded-3xl border border-white/10 bg-white/[0.025] p-7 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all duration-300 flex flex-col"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shadow-[0_8px_20px_rgba(0,229,255,0.2)] mb-5">
                    <Icon className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed flex-1">
                    {s.description}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {s.benefits.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-sm text-white/70"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-white/5">
                    {detailed ? (
                      <Link
                        to={`/services/${s.slug}`}
                        data-testid={`services-link-${i}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        Explore Service <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : (
                      <span className="text-xs text-white/35">
                        Detailed page coming soon
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
